import { Color, Spacing } from '@persona-id/design-tokens';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Params, useNavigate, useParams } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { TabbedSection } from 'dashboard/components/TabbedSectionV2';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavBreadcrumb from 'dashboard/components-lib/NavBreadcrumb';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  FULL_SCREEN_HEADER_HEIGHT,
  FullScreenHeaderContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { CaseTemplate } from 'dashboard/models/case-template';
import {
  CaseVariableDefinition,
  CaseVariableDefinitionAttributes,
} from 'dashboard/models/case-variable-definition';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useUpdateCaseVariableDefinitionMutation } from 'dashboard/redux/api/case-variable-definition';
import CaseTemplateGeneralQueryEditor from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateQueryEditor/CaseTemplateGeneralQueryEditor';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledBackdrop = styled(Backdrop)`
  background: ${Color.BackgroundPrimary};
  padding: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  padding: ${Spacing[7]} ${Spacing[5]};
  overflow: auto;
`;

interface CaseTemplateSettingsProps {
  caseTemplate: CaseTemplate;
  dataSources: ExternalIntegrationDataSource[];
  isLoadingCaseVariableDefinitions: boolean;
  caseVariableDefinitions: CaseVariableDefinition[];
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
}

const CaseTemplateQueryEditor: FC<CaseTemplateSettingsProps> = ({
  dataSources,
  isLoadingCaseVariableDefinitions,
  caseTemplate,
  caseVariableDefinitions,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { caseVariableDefinitionId } = useParams<Params>() as Params;

  const matchingDefinition = useMemo(
    () => caseVariableDefinitions.find((cvd) => cvd.id === caseVariableDefinitionId),
    [caseVariableDefinitions, caseVariableDefinitionId],
  );

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    watch,
    formState: { isDirty },
  } = useFormSecurely<CaseVariableDefinitionAttributes>({
    defaultValues: matchingDefinition?.attributes,
  });

  const [updateCaseVariableDefinition, { error: updateCaseVariableDefinitionError }] =
    useUpdateCaseVariableDefinitionMutation();

  useRTKErrorHandler({
    error: updateCaseVariableDefinitionError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  useEffect(() => {
    if (matchingDefinition?.attributes) {
      reset(matchingDefinition.attributes);
    }
  }, [matchingDefinition?.attributes, reset]);

  const handleBackClick = useCallback(() => {
    navigate(`/dashboard/case-templates/${caseTemplate.id}/settings`);
  }, [navigate, caseTemplate.id]);

  const handleUpdateCaseVariableDefinition = useCallback(
    async (data) => {
      if (matchingDefinition) {
        const result = await updateCaseVariableDefinition({
          id: matchingDefinition.id,
          type: ApiModel.CaseVariableDefinition,
          attributes: {
            ...matchingDefinition.attributes,
            key: data.key,
            'variable-definition': {
              ...data['variable-definition'],
              // TODO(Ernest): temporarily double write the key to layout variable definition until we remove it entirely
              key: data.key,
            },
          },
        });

        if ('data' in result) {
          dispatch(showMessage('Successfully saved case query.', MessageType.Success));
          navigate(`/dashboard/case-templates/${caseTemplate.id}/settings`);
        }
      }
    },
    [matchingDefinition, updateCaseVariableDefinition, dispatch, navigate, caseTemplate.id],
  );

  if (isLoadingCaseVariableDefinitions) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  }

  if (!matchingDefinition) {
    return (
      <Backdrop>
        <NullState message='Failed to load the query editor' />
      </Backdrop>
    );
  }
  return (
    <form onSubmit={handleSubmit(handleUpdateCaseVariableDefinition)}>
      <FullScreenHeaderContainer data-test='case-template-query-editor__header'>
        <Controller
          name='key'
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <NavBreadcrumb
                name={value}
                breadcrumbItems={[]}
                onBackClick={handleBackClick}
                editable
                onNameUpdate={onChange}
              />
            );
          }}
        />
        <HStack>
          <Button
            id='case-template-query-editor__save-button'
            data-test='case-template-query-editor__save-button'
            disabled={!isDirty}
            type='submit'
          >
            Save
          </Button>
        </HStack>
      </FullScreenHeaderContainer>
      <StyledBackdrop data-test='case-template-query-editor'>
        <Container>
          <TabbedSection
            cardView={true}
            tabs={[
              {
                name: 'General',
                contents: (
                  <CaseTemplateGeneralQueryEditor
                    caseVariableDefinitions={caseVariableDefinitions}
                    dataSources={dataSources}
                    control={control}
                    watch={watch}
                    resetField={resetField}
                  />
                ),
              },
            ]}
          />
        </Container>
      </StyledBackdrop>
    </form>
  );
};

export default CaseTemplateQueryEditor;
